import { User, UserSubscriptionNested } from '@/client';
import { formatDate } from '@/utils/dateUtils';

/**
 * Checks if the user has a standard account type.
 * Replaces the old "permanent" user_type check.
 */
export const isStandardAccount = (user: User | null): boolean => {
	return !!user && user.user_type === 'standard';
};

/**
 * Share-path temporary (shadow) accounts: authenticated users created by a share or transfer who
 * have not set login credentials yet. The backend upgrades them to 'standard' once they set a
 * password (see SecuritySection's set-password flow).
 */
export const isTemporaryAccount = (user: User | null): boolean => {
	return !!user && user.user_type === 'temporary';
};

/**
 * Fully registered accounts can acquire properties (add, claim, buy). Guests and share-path
 * temporary accounts can only access what was shared with them until they register.
 */
export const isFullyRegisteredAccount = (user: User | null): boolean => {
	if (!user) return false;
	return !isTemporaryAccount(user);
};

/** Stripe subscription lifecycle statuses we surface in the UI. */
export const SubscriptionStatuses = {
	Active: 'active',
	Trialing: 'trialing',
	PastDue: 'past_due',
	Unpaid: 'unpaid',
	Canceled: 'canceled',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatuses)[keyof typeof SubscriptionStatuses];

/** Subscription tiers we distinguish in the UI. */
export const SubscriptionTypes = {
	Pro: 'pro',
} as const;
export type SubscriptionType = (typeof SubscriptionTypes)[keyof typeof SubscriptionTypes];

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
	[SubscriptionStatuses.Active]: 'Active',
	[SubscriptionStatuses.Trialing]: 'Trialing',
	[SubscriptionStatuses.PastDue]: 'Past due',
	[SubscriptionStatuses.Unpaid]: 'Payment required',
	[SubscriptionStatuses.Canceled]: 'Canceled',
};

// TODO: Surface active subscriptions scheduled to cancel once the API exposes
// `cancel_at_period_end` or `cancel_at`; Stripe keeps those subscriptions `active`
// until the paid period ends, so status/current_period_end alone cannot identify them.

/** Human-readable label for a subscription status, falling back to the raw status. */
export const getSubscriptionStatusLabel = (status: string): string => SUBSCRIPTION_STATUS_LABELS[status] ?? status;

const hasFuturePeriodEnd = (subscription: UserSubscriptionNested): boolean => {
	if (!subscription.current_period_end) return false;
	return new Date(subscription.current_period_end).getTime() > Date.now();
};

/** True when the user has an active or trialing pro subscription. */
export const hasProActiveSubscription = (user: User | null): boolean => {
	if (!user) return false;
	const sub: UserSubscriptionNested | null | undefined = user.user_subscription;
	if (sub?.type !== SubscriptionTypes.Pro) return false;
	return (
		sub.status === SubscriptionStatuses.Active ||
		sub.status === SubscriptionStatuses.Trialing ||
		(sub.status === SubscriptionStatuses.Canceled && hasFuturePeriodEnd(sub))
	);
};

/**
 * True when the user has reached the external-bundle threshold and has not yet
 * subscribed. The first external bundle is free; 2 or more require a Pro subscription.
 * Super admins are never paywalled.
 */
export const needsProUpgrade = (user: User | null, externalBundleCount: number): boolean => {
	if (!user) return false;
	if (user.is_admin) return false;
	return externalBundleCount >= 2 && !hasProActiveSubscription(user);
};

/**
 * A pro subscription whose status is worth surfacing in billing (active, trialing, or needing
 * payment attention). Acts as a type guard so callers can read the subscription afterward.
 */
export const isManageableProSubscription = (
	subscription: UserSubscriptionNested | null | undefined,
): subscription is UserSubscriptionNested => {
	if (subscription?.type !== SubscriptionTypes.Pro) return false;
	return (
		subscription.status === SubscriptionStatuses.Active ||
		subscription.status === SubscriptionStatuses.Trialing ||
		subscription.status === SubscriptionStatuses.PastDue ||
		subscription.status === SubscriptionStatuses.Unpaid ||
		(subscription.status === SubscriptionStatuses.Canceled && hasFuturePeriodEnd(subscription))
	);
};

/** Color palette for the subscription status badge. */
export const getSubscriptionStatusPalette = (status: string): 'green' | 'red' | 'gray' => {
	switch (status) {
		case SubscriptionStatuses.Active:
		case SubscriptionStatuses.Trialing:
			return 'green';
		case SubscriptionStatuses.PastDue:
		case SubscriptionStatuses.Unpaid:
			return 'red';
		case SubscriptionStatuses.Canceled:
			return 'gray';
		default:
			return 'gray';
	}
};

/** Supporting line under the subscription status — renewal date, or a payment nudge. */
export const getSubscriptionDescription = (subscription: UserSubscriptionNested): string | null => {
	switch (subscription.status) {
		case SubscriptionStatuses.Active:
		case SubscriptionStatuses.Trialing:
			if (!subscription.current_period_end) return null;
			return subscription.cancel_at_period_end
				? `Cancels on ${formatDate(subscription.current_period_end)}`
				: `Renews on ${formatDate(subscription.current_period_end)}`;
		case SubscriptionStatuses.PastDue:
		case SubscriptionStatuses.Unpaid:
			return 'Your Pro subscription needs payment attention.';
		case SubscriptionStatuses.Canceled:
			return subscription.current_period_end
				? `Access until: ${formatDate(subscription.current_period_end)}`
				: null;
		default:
			return null;
	}
};

/** Determines whether the user still needs to complete their profile. */
export const hasIncompleteProfile = (user: User | null): boolean => {
	if (!user) return false;
	return !user.first_name;
};
